<template>
  <div>
    <Modal
      ref="modalRef"
      :title="d['Salary.Recalculate.Title']?.item2"
      :has-info-button="false"
      @close="handleModalClose"
      @handleModalClose="handleModalClose"
    >
      <template v-slot>
        <div class="form-content">
          <div v-for="question in questions" :key="question.content.id">
            <div class="label-container">
              <label :for="dropdownVals(question).stateKey">{{
                d[question.content.properties.questionText].item2
              }}</label>
              <Transition mode="out-in">
                <span
                  v-if="
                    errors[dropdownVals(question).stateKey] ||
                    errors[CustomNumeric(question).stateKey] ||
                    errors[dropdownVals(question).stateKey]
                  "
                  class="error"
                >
                  {{
                    errors[dropdownVals(question).stateKey] ||
                    errors[CustomNumeric(question).stateKey] ||
                    errors[dropdownVals(question).stateKey]
                  }}
                </span></Transition
              >
            </div>
            <DropDownRaw
              v-if="isDropDown(question)"
              :options="dropdownVals(question).options"
              :stateKey="dropdownVals(question).stateKey"
              @input-change="onInputChange"
            />

            <NumberSliderRaw
              v-if="isNumeric(question)"
              :max="CustomNumeric(question).max"
              :min="CustomNumeric(question).min"
              :maxLabel="CustomNumeric(question).maxLabel"
              :minLabel="CustomNumeric(question).minLabel"
              :stateKey="CustomNumeric(question).stateKey"
              @input="onInputChange"
            ></NumberSliderRaw>
            <RadioChoiceRaw
              v-if="isRadio(question)"
              :options="dropdownVals(question).options"
              :name="question.content.properties.questionType"
              :stateKey="dropdownVals(question).stateKey"
              @input="onInputChange"
            />
          </div>
          <button
            class="button"
            @click="submit"
            :disabled="!isValid || reSubmitLoading"
            :class="{ 'loading-state': reSubmitLoading }"
          >
            <span v-if="!reSubmitLoading">{{ d['Salary.Recalculate.CTA'].item2 }}</span>
            <Transition><LoadingSpinner v-if="reSubmitLoading" /></Transition>
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { state, localDictionary, getCalculatedSalaryEstimatePayload } from '@/lib/state'
import DropDownRaw from './raw/DropDownRaw.vue'
import NumberSliderRaw from './raw/NumberSliderRaw.vue'
import RadioChoiceRaw from './raw/RadioChoiceRaw.vue'
import { questionTypeToRawComponent, getRelatedWorkFunctions } from '@/lib/mapping'
import Modal from '@/components/Modal.vue'
import LoadingSpinner from '@/components/loading/LoadingSpinner.vue'
import { getCalculatedSalaryEstimate } from '@/services/user-data-service'

export default {
  props: {
    recalc: Boolean,
  },
  data: function () {
    return {
      questions: state.questions,
      d: localDictionary(),
      questionTypeToRawComponent,
      lists: state.lists,
      listKeys: state.listKeys,
      initialAnswers: state.answers,
      isValid: false,
      errors: {},
      reSubmitLoading: false,
    }
  },
  components: {
    DropDownRaw,
    NumberSliderRaw,
    RadioChoiceRaw,
    Modal,
    LoadingSpinner,
  },

  watch: {
    recalc(newVal) {
      if (newVal === true) {
        this.$refs.modalRef.toggleModal()
        this.isValid = false
        this.initialAnswers = state.answers
      }
    },
    reSubmitLoading(newVal) {
      if (!newVal) {
        this.$refs.modalRef.toggleModal() // Close modal when loading is false
      }
    },
  },
  methods: {
    dropdownVals(question) {
      if (!question?.content?.properties) {
        return { options: [], stateKey: '' }
      }

      const stateKey = question.content.properties.questionText
      const optionKey = this.listKeys[stateKey]
      let options = this.lists?.[optionKey] || []

      // special case for work function that are nested under the workareas
      if (optionKey === 'position') {
        const selectedWorkAreaId = state.answers['Salary.Questions.WorkArea']
        options = getRelatedWorkFunctions(selectedWorkAreaId)
      }

      return {
        options,
        stateKey,
      }
    },
    CustomNumeric(question) {
      return {
        min: question.content.properties.numberRangeFrom,
        max: question.content.properties.numberRangeTo,
        step: 1,
        minLabel: question.content.properties.numberRangeFromLabel,
        maxLabel: question.content.properties.numberRangeToLabel,
        stateKey: question.content.properties.questionText,
      }
    },
    isRadio(question) {
      return this.questionTypeToRawComponent[question.content.properties.questionType] === 'Radio'
    },
    isDropDown(question) {
      return (
        this.questionTypeToRawComponent[question.content.properties.questionType] === 'DropDown'
      )
    },
    isNumeric(question) {
      if (question.content.properties.questionType === 'Custom') {
        return question.content.properties.customType === 'Number'
      }
      return false
    },
    async submit() {
      const payload = getCalculatedSalaryEstimatePayload()

      try {
        this.reSubmitLoading = true
        const data = await getCalculatedSalaryEstimate(payload)
        state.resultData = data
        this.handleModalClose()
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      } catch (error) {
        console.error('Error submitting data:', error)
      } finally {
        this.reSubmitLoading = false
      }
    },
    handleModalClose() {
      this.$emit('update:recalc', false)
    },
    onInputChange(stateKey) {
      // If statekey is WorkArea we also need to update Sector before submitting
      if (stateKey === 'Salary.Questions.WorkArea') {
        state.answers['Salary.Questions.WorkFunction'] = ''
        this.isValid = false
        this.checkValid()
      } else {
        this.checkValid()
      }
    },
    checkValid() {
      this.errors = {}
      // Validate each field
      Object.keys(state.answers).forEach((key) => {
        const question = this.questions.find((q) => q.content.properties.questionText === key)
        if (
          question?.content?.properties?.obligatory &&
          (state.answers[key] === '' || state.answers[key] == null)
        ) {
          if (
            question?.content?.properties?.obligatory &&
            (state.answers[key] === '' || state.answers[key] == null)
          ) {
            this.errors[key] = this.d['Salary.Recalculate.Required']?.item2
          }
        }
      })
      // Update isValid based on errors
      this.isValid = Object.keys(this.errors).length === 0
    },
  },
}
</script>
<style lang="scss" scoped>
label {
  margin-bottom: 0px;
  line-height: 24px;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-bottom: 48px;

  & > button {
    position: relative;
    display: flex;
    gap: 8px;
    align-self: center;
    transition: all 0.5s ease;
  }
}

.label-container {
  display: flex;
  gap: 12px;
}

.error {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}

.v-enter-active,
.v-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}
</style>
